import { useCallback, useContext, useEffect, useState } from "react";
import CustomSpinner from "../components/CustomSpinner";
import UserContext from "../context/UserContext";
import transactionService from "../services/transactionService";
import categoryService from "../services/categoryService";
import { Navigate } from "react-router-dom";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "MXN" }).format(
    amount ?? 0
  );

const getCurrentMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${now.getFullYear()}-${month}`;
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatTransactionDate = (value) => {
  if (!value) return "-";

  const dateValue = typeof value === "string" ? value.split("T")[0] : null;
  if (dateValue) {
    const [year, month, day] = dateValue.split("-");
    return `${day}/${month}/${year}`;
  }

  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-ES");
};

const getMonthRange = (yearMonth) => {
  const [year, month] = yearMonth.split("-").map(Number);
  const startDate = toDateInputValue(new Date(year, month - 1, 1));
  const endDate = toDateInputValue(new Date(year, month, 0));
  return { startDate, endDate };
};

const getFortnightRange = (yearMonth, fortnight) => {
  const [year, month] = yearMonth.split("-").map(Number);
  const startDate = toDateInputValue(new Date(
    year,
    month - 1,
    fortnight === "first" ? 1 : 16
  ));
  const endDate = toDateInputValue(new Date(
    year,
    fortnight === "first" ? month - 1 : month,
    fortnight === "first" ? 15 : 0
  ));
  return { startDate, endDate };
};

const getCurrentYear = () => String(new Date().getFullYear());

const getYearRange = (year) => {
  const startDate = toDateInputValue(new Date(Number(year), 0, 1));
  const endDate = toDateInputValue(new Date(Number(year), 11, 31));
  return { startDate, endDate };
};

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y -= 1) {
    years.push(String(y));
  }
  return years;
};

const TYPE_LABEL = { income: "Ingreso", expense: "Gasto" };

export default function Reports() {
  const { user } = useContext(UserContext);

  const [viewMode, setViewMode] = useState("month");
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedFortnight, setSelectedFortnight] = useState("first");
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const defaultRange = getMonthRange(getCurrentMonth());
  const [summary, setSummary] = useState({
    totalIncome: 0,
    totalExpense: 0,
    balance: 0,
    byCategory: [],
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [categoryStartDate, setCategoryStartDate] = useState(defaultRange.startDate);
  const [categoryEndDate, setCategoryEndDate] = useState(defaultRange.endDate);

  const buildSummaryFromTransactions = (txs) => {
    let totalIncome = 0;
    let totalExpense = 0;
    const categoryTotals = {};

    txs.forEach((tx) => {
      const amount = Number(tx.amount) || 0;
      if (tx.type === "income") {
        totalIncome += amount;
      } else if (tx.type === "expense") {
        totalExpense += amount;
        const key = tx.categoryId || "sin-categoria";
        if (!categoryTotals[key]) {
          categoryTotals[key] = {
            categoryId: key,
            name: tx.categoryName || "Sin categoría",
            type: "expense",
            amount: 0,
          };
        }
        categoryTotals[key].amount += amount;
      }
    });

    const byCategory = Object.values(categoryTotals).sort(
      (a, b) => b.amount - a.amount
    );

    return {
      totalIncome,
      totalExpense,
      balance: totalIncome - totalExpense,
      byCategory,
    };
  };

  const loadData = useCallback(
    async (mode, month, year) => {
      if (!user) return;
      setLoading(true);
      try {
        if (mode === "month") {
          const { startDate, endDate } = getMonthRange(month);
          const [summaryRes, txRes] = await Promise.all([
            transactionService.getSummary(month),
            transactionService.getTransactions(startDate, endDate),
          ]);
          setSummary(summaryRes.data);
          setTransactions(txRes.data);
        } else if (mode === "year") {
          const { startDate, endDate } = getYearRange(year);
          const txRes = await transactionService.getTransactions(startDate, endDate);
          setTransactions(txRes.data);
          setSummary(buildSummaryFromTransactions(txRes.data));
        } else if (mode === "fortnight") {
          const { startDate, endDate } = getFortnightRange(month, selectedFortnight);
          const txRes = await transactionService.getTransactions(startDate, endDate);
          setTransactions(txRes.data);
          setSummary(buildSummaryFromTransactions(txRes.data));
        } else if (mode === "category") {
          // traer transacciones filtradas por categoría y rango de fechas
          const txRes = await transactionService.getTransactions(categoryStartDate, categoryEndDate, selectedCategoryId);
          setTransactions(txRes.data);
          setSummary(buildSummaryFromTransactions(txRes.data));
        }
      } catch (error) {
        console.error("Error cargando reporte:", error);
      } finally {
        setLoading(false);
      }
    },
    [user, categoryStartDate, categoryEndDate, selectedCategoryId, selectedFortnight]
  );

  useEffect(() => {
    loadData(viewMode, selectedMonth, selectedYear);
  }, [viewMode, selectedMonth, selectedYear, categoryStartDate, categoryEndDate, selectedCategoryId, loadData]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await categoryService.getCategories();
        setCategories(res.data || []);
      } catch (e) {
        console.error("Error cargando categorías:", e);
      }
    };
    fetchCategories();
  }, []);

  const handleExportCSV = () => {
    const header = "Fecha,Descripción,Categoría,Tipo,Monto";
    const rows = transactions.map((tx) => {
      const date = formatTransactionDate(tx.date);
      const desc = (tx.description || "").replace(/,/g, ";");
      const cat = (tx.categoryName || "-").replace(/,/g, ";");
      const type = TYPE_LABEL[tx.type] || tx.type;
      const amount = tx.type === "expense" ? -Math.abs(tx.amount) : tx.amount;
      return `${date},${desc},${cat},${type},${amount}`;
    });
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const filenamePart = viewMode === "month" ? selectedMonth : selectedYear;
    link.download = `reporte-${filenamePart}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!user) return <Navigate to="/" replace />;

  const sortedCategories = [...summary.byCategory].sort(
    (a, b) => b.amount - a.amount
  );
  const totalAll = sortedCategories.reduce((s, c) => s + c.amount, 0) || 1;

  return (
    <>
      {loading && <CustomSpinner />}
      <div className="min-h-[91vh] banner-1 px-4 py-6 text-slate-100 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-3xl border border-white/10 bg-slate-900/80 p-4 shadow-2xl shadow-black/20 sm:p-6">
        <h1 className="mb-4 text-2xl font-semibold">Reportes</h1>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-300">Filtro</label>
              <div className="flex rounded-xl border border-slate-700 bg-slate-950 p-1">
                <button
                  onClick={() => setViewMode("month")}
                  className={`rounded-lg px-3 py-2 text-sm transition ${viewMode === "month" ? "bg-sky-500 text-white" : "text-slate-300 hover:bg-slate-800"}`}
                >
                  Por mes
                </button>
                <button
                  onClick={() => setViewMode("year")}
                  className={`rounded-lg px-3 py-2 text-sm transition ${viewMode === "year" ? "bg-sky-500 text-white" : "text-slate-300 hover:bg-slate-800"}`}
                >
                  Por año
                </button>
                <button
                  onClick={() => setViewMode("fortnight")}
                  className={`rounded-lg px-3 py-2 text-sm transition ${viewMode === "fortnight" ? "bg-sky-500 text-white" : "text-slate-300 hover:bg-slate-800"}`}
                >
                  Por quincena
                </button>
                <button
                  onClick={() => setViewMode("category")}
                  className={`rounded-lg px-3 py-2 text-sm transition ${viewMode === "category" ? "bg-sky-500 text-white" : "text-slate-300 hover:bg-slate-800"}`}
                >
                  Por categoría
                </button>
              </div>
            </div>

            {viewMode === "month" || viewMode === "fortnight" ? (
              <>
              <label className="text-sm text-slate-300">
                <span className="mb-2 block">Seleccionar mes</span>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
                />
              </label>
              {viewMode === "fortnight" && (
                <label className="text-sm text-slate-300">
                  <span className="mb-2 block">Seleccionar quincena</span>
                  <select
                    value={selectedFortnight}
                    onChange={(e) => setSelectedFortnight(e.target.value)}
                    className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
                  >
                    <option value="first">Primera quincena</option>
                    <option value="second">Segunda quincena</option>
                  </select>
                </label>
              )}
              </>
            ) : viewMode === "year" ? (
              <label className="text-sm text-slate-300">
                <span className="mb-2 block">Seleccionar año</span>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
                >
                  {getYearOptions().map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-sm text-slate-300">
                  <span className="mb-2 block">Seleccionar categoría</span>
                  <select
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
                  >
                    <option value="">Todas las categorías</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm text-slate-300">
                  <span className="mb-2 block">Desde</span>
                  <input
                    type="date"
                    value={categoryStartDate}
                    onChange={(e) => setCategoryStartDate(e.target.value)}
                    className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
                  />
                </label>
                <label className="text-sm text-slate-300">
                  <span className="mb-2 block">Hasta</span>
                  <input
                    type="date"
                    value={categoryEndDate}
                    onChange={(e) => setCategoryEndDate(e.target.value)}
                    className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
                  />
                </label>
              </div>
            )}
          </div>

          <div className="mb-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl bg-emerald-500/15 p-4 ring-1 ring-emerald-400/25">
              <div className="text-sm text-emerald-200">Ingresos</div>
              <div className="mt-2 text-2xl font-semibold">{formatCurrency(summary.totalIncome)}</div>
            </div>
            <div className="rounded-2xl bg-rose-500/15 p-4 ring-1 ring-rose-400/25">
              <div className="text-sm text-rose-200">Gastos</div>
              <div className="mt-2 text-2xl font-semibold">{formatCurrency(summary.totalExpense)}</div>
            </div>
            <div className="rounded-2xl bg-sky-500/15 p-4 ring-1 ring-sky-400/25">
              <div className="text-sm text-sky-200">Balance</div>
              <div className={`mt-2 text-2xl font-semibold ${summary.balance < 0 ? "text-rose-400" : "text-white"}`}>
                {formatCurrency(summary.balance)}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h5 className="mb-3 text-lg font-semibold">Desglose por categoría</h5>
            {sortedCategories.length === 0 ? (
              <div className="rounded-2xl border border-slate-700 bg-slate-950/60 p-4 text-sm text-slate-400">Sin movimientos en este período.</div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-white/10">
                <table className="min-w-[620px] w-full divide-y divide-white/10 text-sm">
                  <thead className="bg-slate-800/80 text-left text-slate-300">
                    <tr>
                      <th className="px-3 py-3">Categoría</th>
                      <th className="px-3 py-3">Tipo</th>
                      <th className="px-3 py-3 text-right">Total</th>
                      <th className="px-3 py-3">% del total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10 bg-slate-900/50">
                    {sortedCategories.map((item) => {
                      const pct = Math.round((item.amount / totalAll) * 100);
                      const isIncome = item.type === "income";
                      return (
                        <tr key={item.categoryId}>
                          <td className="px-3 py-3">{item.name}</td>
                          <td className="px-3 py-3">
                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${isIncome ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
                              {TYPE_LABEL[item.type] || item.type}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right font-semibold text-slate-100">{formatCurrency(item.amount)}</td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-800">
                                <div className={`h-full rounded-full ${isIncome ? "bg-emerald-500" : "bg-rose-500"}`} style={{ width: `${pct}%` }} />
                              </div>
                              <span className="w-9 text-right text-xs text-slate-400">{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mb-4">
            <h5 className="mb-3 text-lg font-semibold">Transacciones</h5>
            {transactions.length === 0 ? (
              <div className="rounded-2xl border border-slate-700 bg-slate-950/60 p-4 text-sm text-slate-400">Sin movimientos en este período.</div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-white/10">
                <table className="min-w-[620px] w-full divide-y divide-white/10 text-sm">
                  <thead className="bg-slate-800/80 text-left text-slate-300">
                    <tr>
                      <th className="px-3 py-3">Fecha</th>
                      <th className="px-3 py-3">Descripción</th>
                      <th className="px-3 py-3">Categoría</th>
                      <th className="px-3 py-3">Tipo</th>
                      <th className="px-3 py-3 text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10 bg-slate-900/50">
                    {transactions.map((tx) => {
                      const date = formatTransactionDate(tx.date);
                      const desc = tx.description || "-";
                      const cat = tx.categoryName || "-";
                      const amount = tx.type === "expense" ? -Math.abs(tx.amount) : tx.amount;
                      const isIncome = tx.type === "income";
                      return (
                        <tr key={tx.id}>
                          <td className="px-3 py-3">{date}</td>
                          <td className="px-3 py-3">{desc}</td>
                          <td className="px-3 py-3">{cat}</td>
                          <td className="px-3 py-3">
                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${isIncome ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
                              {TYPE_LABEL[tx.type] || tx.type}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right font-semibold text-slate-100">{formatCurrency(amount)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleExportCSV}
              disabled={transactions.length === 0}
              className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              📥 Exportar CSV
            </button>
            {transactions.length === 0 && <span className="text-sm text-slate-400">Sin transacciones para exportar.</span>}
          </div>
        </div>
      </div>
    </>
  );
}